import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './LoginModal.css';

interface User {
    id: string;
    username: string;
    email: string;
    role: 'ADMIN' | 'USER' | 'FREE' | 'TRIAL';
    isActive: boolean;
}

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLogin: (user: User, token: string) => void;
    apiBaseUrl: string;
    mode: 'login' | 'signup';
    onModeChange?: (mode: 'login' | 'signup') => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLogin, apiBaseUrl, mode, onModeChange }) => {
    const { t } = useTranslation();
    const isSignUp = mode === 'signup';
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [resetLinkSent, setResetLinkSent] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (isSignUp) {
                // Validate sign up form
                if (!formData.username || !formData.password) {
                    setError(t('auth.emailRequired'));
                    setLoading(false);
                    return;
                }

                if (formData.password !== formData.confirmPassword) {
                    setError(t('auth.passwordsDoNotMatch'));
                    setLoading(false);
                    return;
                }

                // OWASP compatible password validation
                const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{12,}$/;
                if (!passwordRegex.test(formData.password)) {
                    setError(t('auth.passwordPolicy'));
                    setLoading(false);
                    return;
                }

                // Email validation
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.username)) {
                    setError(t('auth.invalidEmail'));
                    setLoading(false);
                    return;
                }

                // Register user
                const response = await fetch(`${apiBaseUrl}/auth/register`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: formData.username,
                        password: formData.password
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || t('auth.registrationFailed'));
                }

                // Show verification sent message
                setVerificationSent(true);
                setRegisteredEmail(formData.username);
                setFormData({ username: '', password: '', confirmPassword: '' });
            } else {
                // Login
                const response = await fetch(`${apiBaseUrl}/auth/login`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        username: formData.username,
                        password: formData.password,
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    if (data.requiresVerification) {
                        setError(t('auth.verificationRequired'));
                        setRegisteredEmail(data.email || formData.username);
                        setVerificationSent(true);
                    } else {
                        throw new Error(data.error || t('auth.loginFailed'));
                    }
                    return;
                }

                // Successful login
                onLogin(data.user, data.token);
                onClose();
            }
        } catch (err: any) {
            setError(err.message || t('errors.anErrorOccurred'));
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${apiBaseUrl}/auth/resend-verification`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: registeredEmail
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || t('verification.failedToResend'));
            }

            setError(t('verification.verificationEmailSent'));
        } catch (err: any) {
            setError(err.message || t('verification.failedToResend'));
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(`${apiBaseUrl}/auth/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: forgotPasswordEmail
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || t('errors.anErrorOccurred'));
            }

            setResetLinkSent(true);
        } catch (err: any) {
            setError(err.message || t('errors.anErrorOccurred'));
        } finally {
            setLoading(false);
        }
    };

    const toggleMode = () => {
        const newMode = isSignUp ? 'login' : 'signup';
        if (onModeChange) {
            onModeChange(newMode);
        }
        setError('');
        setVerificationSent(false);
        setShowForgotPassword(false);
        setResetLinkSent(false);
        setForgotPasswordEmail('');
        setFormData({ username: '', password: '', confirmPassword: '' });
    };

    const goBackToLogin = () => {
        setShowForgotPassword(false);
        setResetLinkSent(false);
        setForgotPasswordEmail('');
        setError('');
    };

    if (!isOpen) return null;

    return (
        <div className="login-modal-overlay" onClick={onClose}>
            <div className="login-modal" onClick={(e) => e.stopPropagation()}>
                <div className="login-modal-header">
                    <h2>
                        {verificationSent ? t('verification.emailSent') :
                         resetLinkSent ? t('auth.resetLinkSent') :
                         showForgotPassword ? t('auth.forgotPasswordTitle') :
                         (isSignUp ? t('auth.signup') : t('auth.login'))
                        }
                    </h2>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>

                {verificationSent ? (
                    <div className="verification-message">
                        <p>{t('verification.verificationSent')} <strong>{registeredEmail}</strong>.</p>
                        <p>{t('verification.checkInbox')}</p>
                        <p>{t('verification.afterVerification')}</p>
                        <button 
                            className="submit-button" 
                            onClick={handleResendVerification}
                            disabled={loading}
                        >
                            {loading ? t('verification.sending') : t('verification.resendVerification')}
                        </button>
                        <button 
                            className="toggle-mode-button" 
                            onClick={() => {
                                setVerificationSent(false);
                                if (onModeChange) {
                                    onModeChange('login');
                                }
                            }}
                        >
                            {t('verification.backToLogin')}
                        </button>
                    </div>
                ) : resetLinkSent ? (
                    <div className="verification-message">
                        <p>{t('auth.resetLinkSent')}</p>
                        <button 
                            className="toggle-mode-button" 
                            onClick={goBackToLogin}
                        >
                            {t('auth.backToLogin')}
                        </button>
                    </div>
                ) : showForgotPassword ? (
                    <>
                        <div className="forgot-password-instructions">
                            <p>{t('auth.forgotPasswordInstructions')}</p>
                        </div>
                        <form className="login-form" onSubmit={handleForgotPassword}>
                            <div className="form-group">
                                <label htmlFor="forgotPasswordEmail">{t('auth.email')}</label>
                                <input
                                    type="email"
                                    id="forgotPasswordEmail"
                                    value={forgotPasswordEmail}
                                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                    placeholder={t('auth.enterEmail')}
                                    required
                                    autoComplete="email"
                                />
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            <button 
                                type="submit" 
                                className="submit-button" 
                                disabled={loading}
                            >
                                {loading ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
                            </button>
                        </form>

                        <div className="login-modal-footer">
                            <button className="toggle-mode-button" onClick={goBackToLogin}>
                                {t('auth.backToLogin')}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <form className="login-form" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="username">{t('auth.email')}</label>
                                <input
                                    type="email"
                                    id="username"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    placeholder={t('auth.enterEmail')}
                                    required
                                    autoComplete="email"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="password">{t('auth.password')}</label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    placeholder={t('auth.enterPassword')}
                                    required
                                    minLength={12}
                                    autoComplete={isSignUp ? "new-password" : "current-password"}
                                />
                            </div>

                            {isSignUp && (
                                <div className="form-group">
                                    <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        placeholder={t('auth.confirmPasswordPlaceholder')}
                                        required
                                        minLength={12}
                                        autoComplete="new-password"
                                    />
                                </div>
                            )}

                            {!isSignUp && (
                                <div className="forgot-password-link-container" style={{ textAlign: 'right', marginTop: '10px' }}>
                                    <button 
                                        type="button"
                                        className="forgot-password-link"
                                        onClick={() => setShowForgotPassword(true)}
                                        style={{ 
                                            background: 'none', 
                                            border: 'none', 
                                            color: '#007bff', 
                                            textDecoration: 'underline', 
                                            cursor: 'pointer', 
                                            fontSize: '14px' 
                                        }}
                                    >
                                        {t('auth.forgotPassword')}
                                    </button>
                                </div>
                            )}

                            {error && <div className="error-message">{error}</div>}

                            <button 
                                type="submit" 
                                className="submit-button" 
                                disabled={loading}
                            >
                                {loading ? t('auth.processing') : (isSignUp ? t('auth.signup') : t('auth.login'))}
                            </button>
                        </form>

                        <div className="login-modal-footer">
                            <p>
                                {isSignUp ? t('auth.alreadyHaveAccount') : t('auth.dontHaveAccount')}
                            </p>
                            <button className="toggle-mode-button" onClick={toggleMode}>
                                {isSignUp ? t('auth.login') : t('auth.signup')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LoginModal; 
